/**
 * Policy manager for PD
 *
 * Usage:
 * To check for system policy: Policies.hasSubmissionCreatePolicy
 * To check for project policy: Policies.inProject('projectId').hasSubmissionSavePolicy
 * To cache policy if needed: Policies.inProject('projectId', [policies from server]).hasSubmissionSavePolicy
 */
import _ from "lodash";
import { Service } from "puzzle";

type PROJECT_POLICY = {
  project: number,
  policyList: {
    [key: string]: boolean
  }
}

class Policies extends Service {
  private static _instance: Policies;
  private projectPolicies: PROJECT_POLICY[] = [];
  private policyCacheLength: number = 10; // number of projects that will be saved in cache

  hasSubmissionCreatePolicy: boolean = false;
  hasSubmissionSavePolicy: boolean = false;
  hasTemplatesFolderPolicy: boolean = false;
  hasApplyTemplatePolicy: boolean = false;
  hasRequestQuotePolicy: boolean = false;
  hasLookupQuotePolicy: boolean = false;
  hasPromoteDemoteAllPolicy: boolean = false;
  hasPromoteDemoteSelectedFiles: boolean = false;
  hasDeactivateRefreshButton: boolean = false;
  hasDownloadAnalysis: boolean = false;
  systemPoliciesLoaded: boolean = false;
  hasDeliverViaWS: boolean = false;
  hasUploadTranslatable: boolean = false;
  hasMassActions: boolean = false;

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }

  constructor() {
    super();
    this.registerServiceToDispatcher();
  }

  /**
   * Checks if project is in project policy cache
   * @param projectId
   */
  checkPolicyCache = (projectId: number): boolean => {
    return !!_.find(this.projectPolicies, ({ project: projectId }));
  }

  /**
   * Sets project and system policies.
   * If projectId is passed policies are added to project, if not they are set as system.
   *
   * @param policies
   * @param projectId
   */
  setup = (policies: string[], projectId?: number): any => {
    let mappedPolicies: any = {};
    let mappedPolicy: string;
    policies.forEach((policy: string) => {
      mappedPolicy = policiesMap[policy];
      if (mappedPolicy) {
        mappedPolicies[mappedPolicy] = true;
      }
    });

    projectId && !_.isEmpty(mappedPolicies) ? this.projectPolicies.push({
      project: projectId,
      policyList: mappedPolicies
    }) : Object.assign(this, mappedPolicies);
    if (this.projectPolicies.length > this.policyCacheLength) {
      this.projectPolicies.shift();
    }
    this.systemPoliciesLoaded = true;
    return mappedPolicies;
  };

  /**
   * If policies and projectId are passed add them to project, if not returns all policies for passed project.
   *
   * @param projectId
   * @param policies
   */
  inProject = (projectId: number, policies?: string[]): any => {
    let returnValue: PROJECT_POLICY | {} = {};
    if (!_.find(this.projectPolicies, ({ project: projectId })) && policies) {
      this.setup(policies, projectId);
    } else {
      const project = _.find(this.projectPolicies, ({ project: projectId }))
      if (project) {
        returnValue = project.policyList
      }
    }

    return returnValue;
  };

  /**
   * Get policy for show/hide 'Select and Start' button in Select Suppliers dialog
   * @param projectId
   * @param policies
   */

  getProjectPolicy = (projectId: number, policies: string[] | string) => {
    let result = false;
    if (_.isArray(policies)) {
      result = policies.every((policy: string) => {
        return this.inProject(projectId)?.[policy]
      });
    } else {
      result = this.inProject(projectId)?.[policies];
    }

    return result;
  };

  /**
   * Returns value of desired systemPolicy
   * @param policyName
   */
  getSystemPolicy = (policyName: string): boolean => {
    const policyValue = _.get(this, policyName);

    return !!policyValue;
  };

  /**
   * Deletes project configuration's for all projects, and also sets all system policies to default(false)
   */
  public reset = () => {
    this.projectPolicies = [];
    this.hasApplyTemplatePolicy = false;
    this.hasSubmissionCreatePolicy = false;
    this.hasSubmissionSavePolicy = false;
    this.hasTemplatesFolderPolicy = false;
    this.hasRequestQuotePolicy = false;
    this.hasLookupQuotePolicy = false;
    this.hasPromoteDemoteAllPolicy = false;
    this.hasPromoteDemoteSelectedFiles = false;
    this.hasDeactivateRefreshButton = false;
    this.hasDownloadAnalysis = false;
    this.hasMassActions = false;
  };

  /**
   * Sets policy configuration for given project
   * @param policies
   * @param projectId
   */
  setProjectPolicies = (policies: string[], projectId: number) => {
    this.inProject(projectId, policies);
  };

}

/**
 * Mapping object
 * "policy name sent by server":"name used on NUI"
 */
const policiesMap: Record<string, string> = {
  "POLICY_PD_ADD_DELIVERABLE_COLLATERAL": "hasAddDelierableCollateralPolicy",
  "POLICY_PD_ADD_SUBMISSION": "hasSubmissionCreatePolicy",
  "POLICY_PD_ADD_SUBMISSION_FROM_TEMPLATE": "hasSubmissionCreateFromTemplatePolicy",
  "POLICY_PD_ANALYZE_SUBMISSION": "hasSubmissionAnalyzePolicy",
  "POLICY_PD_APPLY_TEMPLATE": "hasApplyTemplatePolicy",
  "POLICY_PD_APPROVE_QUOTE": "hasApproveQuotePolicy",
  "POLICY_PD_ASSIGN_PM": 'hasPdAssignPmPolicy',
  "POLICY_PD_CREATE_SUBMISSION_SAVE_BUTTON": "hasSubmissionSavePolicy",
  "POLICY_PD_CREATE_SUBMISSION_TEMPLATE": "hasTemplateCreatePolicy",
  "POLICY_PD_DEACTIVATE_REFRESH_BUTTON": "hasDeactivateRefreshButton",
  "POLICY_PD_DELETE_ALL_DELIVERABLE_COLLATERAL": "hasDeleteAllDeliverableCollateral",
  "POLICY_PD_DELETE_DELIVERABLE_COLLATERAL": "hasDeleteDeliverableCollateral",
  "POLICY_PD_DOWNLOAD_ANALYSIS": "hasDownloadAnalysis",
  "POLICY_PD_DOWNLOAD_DELIVERABLE": "hasDownloadDeliverablePolicy",
  "POLICY_PD_DOWNLOAD_SOURCE": "hasDownloadSourcePolicy",
  "POLICY_PD_EDIT_CLIENT_VENDORS": "hasEditPreferredVendorsPolicy",
  "POLICY_PD_EDIT_DUE_DATE": "hasEditDueDatePolicy",
  "POLICY_PD_EDIT_PHASE_DUE_DATE": "hasEditPhaseDueDatePolicy",
  "POLICY_PD_ENABLE_CLIENT_SUPPLIER_VIEW": "hasEnableClientSupplerView",
  "POLICY_PD_FORCED_EDIT_DUE_DATE": "hasForcedEditDueDatePolicy",
  "POLICY_PD_LOOKUP_QUOTE": "hasLookupQuotePolicy",
  "POLICY_PD_MENU_VIEWSUBMISSIONS_TEMPLATES": "hasTemplatesFolderPolicy",
  "POLICY_PD_POWER": "hasMassActions",
  "POLICY_PD_PROCESS_PM_TASKS": "hasPdProcessPmTasks",
  "POLICY_PD_PROMOTE_DEMOTE_ALL": "hasPromoteDemoteAllPolicy",
  "POLICY_PD_PROMOTE_DEMOTE_FILES": "hasPromoteDemoteSelectedFiles",
  "POLICY_PD_REDELIVER_VIA_WEB_SERVICES": "hasDeliverViaWS",
  "POLICY_PD_REQUEST_QUOTE": 'hasRequestQuotePolicy',
  "POLICY_PD_SAPPHIRE_HYPERLINK_ASSIGNEE_NAME": "hasHyperlinkAssigneeNamePolicy",
  "POLICY_PD_SAPPHIRE_HYPERLINK_PROJECT_MANAGER_NAME": "hasHyperlinkProjectManagerPolicy",
  "POLICY_PD_SELECT_VENDORS": "hasSelectVendorsButtonsDialogPolicy",
  "POLICY_PD_SHOW_TABLEAU_BUTTON": 'hasTableauButton',
  "POLICY_PD_STARTSUBMISSION": "hasSubmissionStartPolicy",
  "POLICY_PD_SUBMISSIONREFERENCEFILES": "hasSubmissionReferenceFilesPolicy",
  "POLICY_PD_SUBMISSION_ASSIGNSUBMISSION": "hasSelectVendorsPolicy",
  "POLICY_PD_SWITCH_INTERFACE": "hasSwitchInterfacePolicy",
  "POLICY_PD_UPLOAD_TRANSLATABLE": "hasUploadTranslatable",
  "POLICY_PD_TEMPLATE_DUE_DATE_DAYS": 'hasTemplateDueDateDays'
};

const PoliciesManager: any = Policies.Instance;
export default PoliciesManager;
